/*
 * Historia 3 (parcial): Testeo continuo de audiencias.
 * Registro de pruebas de audiencia por conjunto de anuncios (ad_group) y
 * aviso de cuáles llevan más de N semanas (umbral definido en "Umbrales")
 * sin volver a probarse. Ningún conjunto se marca como descartado
 * permanentemente por un resultado negativo — siempre se puede registrar
 * una prueba/re-prueba nueva.
 */
import React, { useCallback, useEffect, useState } from 'react'
import authAxios from '../../../../config/authAxios'
import AudienciaHistorialModal from './AudienciaHistorialModal'

const TIPOS_AUDIENCIA = [
  { value: 'lookalike', label: 'Lookalike / Similar' },
  { value: 'advantage_plus', label: 'Advantage+ / Amplia' },
  { value: 'interes', label: 'Interés' },
  { value: 'retargeting', label: 'Retargeting / Remarketing' },
  { value: 'otro', label: 'Otro' },
]
const tipoLabel = value => TIPOS_AUDIENCIA.find(t => t.value === value)?.label || value || '—'
const resultadoBadgeClass = value => `am-badge am-badge-${{ positivo: 'verde', negativo: 'rojo', mixto: 'amarillo', pendiente: 'sin_datos' }[value] || 'sin_datos'}`
const origenLabel = value => ({ meta: 'Detectado en Meta', tiktok: 'Detectado en TikTok', manual: 'Manual' }[value] || 'Manual')
const origenBadgeClass = value => value === 'manual' ? 'am-badge-sin_datos' : 'am-badge-verde'

const emptyForm = { id_group: '', tipo_audiencia: 'lookalike', tipo_audiencia_detalle: '', fecha_prueba: new Date().toISOString().slice(0, 10), indicador_exito: '', id_prueba_referencia: '' }

const AudienciasAgente = ({ campaignId, isMarketing, onNotice, onError }) => {
  const [semanas, setSemanas] = useState(3)
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [history, setHistory] = useState(null)
  const [sugerencia, setSugerencia] = useState(null)
  const [verPausados, setVerPausados] = useState(false)

  const load = useCallback(() => {
    if (!campaignId) return
    setLoading(true)
    authAxios.get(`/admin/agente-marketing/audiencias?id_camp=${campaignId}`)
      .then(r => { setGroups(r.data?.data?.grupos || []); setSemanas(r.data?.data?.semanas_alerta_audiencia ?? 3) })
      .catch(e => onError(e.response?.data?.msg || 'No se pudo cargar las audiencias.'))
      .finally(() => setLoading(false))
  }, [campaignId, onError])

  useEffect(() => { load() }, [load])

  const openForm = group => {
    setSugerencia(null)
    setForm({ ...emptyForm, id_group: String(group.id_group), id_prueba_referencia: group.ultima_prueba_id ? String(group.ultima_prueba_id) : '' })
    const formEl = document.getElementById('am-audiencia-form')
    if (formEl) formEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
    authAxios.get(`/admin/agente-marketing/audiencias/grupo/${group.id_group}/sugerencia`)
      .then(r => {
        const data = r.data?.data
        if (data?.sugerencia) {
          setSugerencia(data)
          setForm(f => ({ ...f, tipo_audiencia: data.sugerencia }))
        }
      })
      .catch(() => {}) // la sugerencia es un plus, nunca bloquea el formulario si falla
  }

  const submitTest = async event => {
    event.preventDefault()
    setSaving(true)
    try {
      await authAxios.post('/admin/agente-marketing/audiencias/prueba', form)
      onNotice(form.id_prueba_referencia ? 'Re-prueba de audiencia registrada.' : 'Prueba de audiencia registrada.')
      setForm(emptyForm)
      await load()
    } catch (e) { onError(e.response?.data?.msg || 'No se pudo registrar la prueba.') }
    finally { setSaving(false) }
  }

  const completeResult = async group => {
    const resultado = window.prompt('Resultado (positivo / negativo / mixto):', '')
    if (resultado === null) return
    if (!['positivo', 'negativo', 'mixto'].includes(resultado.trim())) {
      onError('El resultado debe ser positivo, negativo o mixto.'); return
    }
    const detalle = window.prompt('Detalle del resultado (opcional):', '') || ''
    try {
      await authAxios.post(`/admin/agente-marketing/audiencias/${group.ultima_prueba_id}/resultado`, { resultado: resultado.trim(), resultado_detalle: detalle })
      onNotice('Resultado registrado.')
      await load()
    } catch (e) { onError(e.response?.data?.msg || 'No se pudo registrar el resultado.') }
  }

  const openHistory = async group => {
    try {
      const r = await authAxios.get(`/admin/agente-marketing/audiencias/grupo/${group.id_group}/historial`)
      setHistory({ nombre: group.nombre, rows: r.data?.data || [] })
    } catch (e) { onError(e.response?.data?.msg || 'No se pudo cargar el historial.') }
  }

  if (!campaignId) return null

  return <div>
    <p className="text-muted">
      El sistema revisa a diario la segmentación real en Meta: si cambia, registra sola una prueba pendiente y la evalúa
      7 días después comparando el rendimiento antes/después (CPL, CPA, tratos, leads). La alerta de <strong>{semanas} semanas</strong> sin
      cambiar (umbral en la pestaña Umbrales) solo avisa en conjuntos cuyos anuncios están en <span className="am-badge am-badge-optimizar">Optimizar</span>.
      Ningún conjunto queda descartado permanentemente por un resultado negativo.
    </p>

    {isMarketing && <form id="am-audiencia-form" onSubmit={submitTest} className="am-detail mb-3">
      <h6>{form.id_prueba_referencia ? 'Registrar re-prueba' : 'Registrar prueba de audiencia'}</h6>
      <div className="form-row">
        <div className="form-group col-md-4">
          <label>Conjunto de anuncios</label>
          <select className="form-control" required value={form.id_group} onChange={e => setForm({ ...form, id_group: e.target.value, id_prueba_referencia: '' })}>
            <option value="">Seleccione</option>
            {groups.map(g => <option key={g.id_group} value={g.id_group}>{g.nombre} · {g.plataforma}</option>)}
          </select>
        </div>
        <div className="form-group col-md-3">
          <label>Tipo de audiencia</label>
          <select className="form-control" value={form.tipo_audiencia} onChange={e => setForm({ ...form, tipo_audiencia: e.target.value })}>
            {TIPOS_AUDIENCIA.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          {sugerencia && <small className="text-muted d-block mt-1">Sugerido por {sugerencia.fuente === 'tiktok' ? 'TikTok' : 'Meta'}: {sugerencia.detalle} Confírmalo o cámbialo si no corresponde.</small>}
        </div>
        <div className="form-group col-md-3">
          <label>Fecha</label>
          <input type="date" className="form-control" required value={form.fecha_prueba} onChange={e => setForm({ ...form, fecha_prueba: e.target.value })} />
        </div>
        <div className="form-group col-md-2 d-flex align-items-end">
          {form.id_prueba_referencia && <small className="text-muted mb-2">Re-prueba de #{form.id_prueba_referencia}</small>}
        </div>
        {form.tipo_audiencia === 'otro' && <div className="form-group col-md-6">
          <label>Detalle (obligatorio para "Otro")</label>
          <input type="text" className="form-control" required value={form.tipo_audiencia_detalle} onChange={e => setForm({ ...form, tipo_audiencia_detalle: e.target.value })} placeholder='Ej. "Custom por lista de correo"' />
        </div>}
        <div className="form-group col-md-6">
          <label>Con qué indicador decides si funcionó</label>
          <input type="text" className="form-control" value={form.indicador_exito} onChange={e => setForm({ ...form, indicador_exito: e.target.value })} placeholder='Ej. "CPL ≤ S/2.50 y %Trato ≥5%"' />
        </div>
      </div>
      <button className="btn btn-primary" disabled={saving}>{saving ? 'Guardando…' : 'Registrar'}</button>
      {form.id_prueba_referencia && <button type="button" className="btn btn-link" onClick={() => setForm(emptyForm)}>Cancelar re-prueba</button>}
    </form>}

    <label className="d-flex align-items-center mb-2" style={{ gap: 6, fontWeight: 'normal' }}>
      <input type="checkbox" checked={verPausados} onChange={e => setVerPausados(e.target.checked)} /> Ver conjuntos pausados
    </label>

    <div className="am-table-wrap">
      <table className="table table-hover am-table">
        <thead><tr><th>Conjunto de anuncios</th><th>Última prueba</th><th>Resultado</th><th>Días sin cambio</th><th></th></tr></thead>
        <tbody>
          {groups.filter(g => verPausados || g.estado === 1).map(g => <tr key={g.id_group}>
            <td>
              <strong>{g.nombre}</strong>
              <small className="d-block text-uppercase">{g.plataforma}{g.estado === 0 && ' · pausado'}</small>
              {g.optimizar && <span className="am-badge am-badge-optimizar mt-1">Optimizar</span>}
            </td>
            <td>{g.fecha_prueba
              ? <>{tipoLabel(g.tipo_audiencia)}{g.tipo_audiencia_detalle ? ` (${g.tipo_audiencia_detalle})` : ''}
                  <span className={`am-badge ml-1 ${origenBadgeClass(g.origen)}`}>{origenLabel(g.origen)}</span>
                  <small className="d-block text-muted">{g.fecha_prueba}</small>
                  {g.cambio_detalle && <small className="d-block text-muted">{g.cambio_detalle}</small>}
                </>
              : <span className="text-muted">Nunca probada</span>}</td>
            <td>{g.fecha_prueba
              ? <><span className={resultadoBadgeClass(g.resultado)}>{g.resultado}</span>
                  {g.resultado === 'pendiente' && g.origen !== 'manual' && <small className="d-block text-muted">se evalúa 7 días después del cambio</small>}
                </>
              : '—'}</td>
            <td>{g.dias_sin_prueba ?? '—'} {g.alerta && <span className="am-badge am-badge-pausar ml-1">⚠ vencida</span>}</td>
            <td className="text-nowrap">
              {isMarketing && <button className="btn btn-sm btn-outline-primary mr-1" onClick={() => openForm(g)}>{g.ultima_prueba_id ? 'Re-probar' : 'Probar'}</button>}
              {isMarketing && g.resultado === 'pendiente' && g.origen === 'manual' && <button className="btn btn-sm btn-outline-secondary mr-1" onClick={() => completeResult(g)}>Completar resultado</button>}
              <button className="btn btn-sm btn-outline-secondary" onClick={() => openHistory(g)}>Historial</button>
            </td>
          </tr>)}
        </tbody>
      </table>
      {!loading && groups.length === 0 && <p className="text-center text-muted">Esta campaña no tiene conjuntos de anuncios vinculados todavía.</p>}
    </div>

    {history && <AudienciaHistorialModal nombre={history.nombre} rows={history.rows} onClose={() => setHistory(null)} />}
  </div>
}

export default AudienciasAgente
